/* eslint-disable react-hooks/exhaustive-deps */
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Collapse, InputAdornment, TextField } from '@mui/material';
import DownloadingIcon from '@mui/icons-material/Downloading';

import type { PaymentErrorCode } from '@models/paymentErrorCodes';

import { useAllPaymentErrorsCodesPaginated } from '@features/paymentErrorCodes/queries';
import { useSetPaginatedPaymentsErrorCodesStore } from '@features/paymentErrorCodes/store';

import PrimaryButton from '@components/PrimaryButton';
import BubbleLoadingAnimation from '@components/BubbleLoadingAnimation';

import s from './UnhandledErrors.module.css';

const UnhandledError = ({ item }: { item: string }) => (
  <div className={s.unhandledError}>{item}</div>
);

type UnhandledErrorsListProps = {
  isLoading: boolean;
  allErrors: PaymentErrorCode[];
  filteredErrors: PaymentErrorCode[];
};

const UnhandledErrorsList = ({
  isLoading,
  allErrors,
  filteredErrors,
}: UnhandledErrorsListProps) => {
  const renderContent = () => {
    if (filteredErrors.length === 0 && allErrors.length > 0)
      return <p className={s.unhandleErrorsInformation}>Nothing were found!</p>;
    if (allErrors.length === 0)
      return (
        <p className={s.unhandleErrorsInformation}>
          Congratz! <br />
          All errors are handled! 🎉
        </p>
      );

    return filteredErrors.map((item) => <UnhandledError key={item.id} item={item.error} />);
  };

  return (
    <>
      <div className={s.unhandledErrorsList}>{renderContent()}</div>
      {isLoading && <BubbleLoadingAnimation />}
    </>
  );
};

function UnhandledErrors() {
  const [searchResults, setSearchResults] = useState<PaymentErrorCode[]>([]);
  const [allErrors, setAllErrors] = useState<PaymentErrorCode[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const { data, isLoading } = useAllPaymentErrorsCodesPaginated(currentPage);
  const searchRef = useRef<HTMLInputElement>(null);
  const setPaginatedErrorsStore = useSetPaginatedPaymentsErrorCodesStore();

  const errorCount = data?.meta.itemCount ?? 0;

  // Set allErrors state when data is fetched
  useEffect(() => {
    if (data && data.data && data.data.length > 0) {
      // Filter out duplicates
      setAllErrors((prevErrors) => {
        const newErrors = data.data.filter(
          (newError) => !prevErrors.some((prevError) => prevError.id === newError.id),
        );
        return [...prevErrors, ...newErrors];
      });
    }
  }, [data]);

  // Set UI state when allErrors state is updated
  useEffect(() => {
    if (searchRef.current?.value !== '') {
      setSearchResults(filteredResults);
      return;
    }

    setSearchResults(allErrors);
    setPaginatedErrorsStore(allErrors);
  }, [allErrors]);

  const filteredResults = useMemo(() => {
    const query = searchRef.current?.value?.toLowerCase() || '';
    return allErrors.filter((error) => error.error.toLowerCase().includes(query));
  }, [allErrors, searchRef.current?.value]);

  const handleSearchChange = useCallback(
    debounce((value: string) => {
      const query = value.toLowerCase();
      const filteredResults = allErrors.filter((error) =>
        error.error.toLowerCase().includes(query),
      );
      setSearchResults(filteredResults);
    }, 300),
    [allErrors],
  );

  const handleLoadMore = () => {
    if (!data || !data.meta.hasNextPage) return;

    setCurrentPage((prev) => prev + 1);
  };

  return (
    <div className={s.unhandledErrors}>
      <button
        type="button"
        className={s.unhandledErrorsHeader}
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}>
        <span className={s.unhandledErrorsTitleGroup}>
          <span className={s.unhandledErrorsTitle}>Unhandled Errors:</span>
          {errorCount > 0 && <span className={s.unhandledErrorsBadge}>{errorCount}</span>}
        </span>
        <KeyboardArrowDownIcon
          className={`${s.unhandledErrorsChevron} ${
            isExpanded ? s.unhandledErrorsChevronOpen : ''
          }`}
        />
      </button>
      <Collapse in={isExpanded}>
        <div className={s.unhandledErrorsBody}>
          <div className={s.unhandledErrorsSearchBox}>
            <TextField
              inputRef={searchRef}
              size="small"
              label="Search by error name"
              variant="outlined"
              className={s.unhandledErrorsSearchInput}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <UnhandledErrorsList
            isLoading={isLoading}
            allErrors={allErrors}
            filteredErrors={searchResults}
          />
          <div className={s.unhandledErrorsActions}>
            <PrimaryButton
              title={'Load More'}
              isDisabled={isLoading || !data?.meta.hasNextPage || allErrors.length === 0}
              customClass={s.unhandledErrorsLoadMoreButton}
              icon={<DownloadingIcon />}
              onClick={handleLoadMore}
            />
          </div>
        </div>
      </Collapse>
    </div>
  );
}

export default UnhandledErrors;
